// ============================================================================
// Activos — qué hacer con un error de guardado (#1638)
// ============================================================================
// Archivo común, idéntico en los dos forks. El API responde 409 con `meta.code`
// y el registro que ya existe en `data`; aquí se ofrece ir a él:
//   · IDENTIFICADOR_DUPLICADO → al activo que ya tiene esa serie;
//   · ES_DISPOSITIVO          → a Dispositivos (D1: los GPS no se duplican);
//   · COMPRA_DUPLICADA        → a la compra con esa factura;
//   · PROVEEDOR_DUPLICADO     → al proveedor con ese RFC.
// Un 422 trae `{ errors: [{ field, code }] }` y se pinta junto a cada campo.
// ============================================================================
import type { TFunction } from "i18next";
import type { ValidationIssue } from "@torre/assets";

import { confirmDialog, notifyError } from "../../../components/ui/dialog";
import { assetsErrorOf } from "../services/activosApi";
import { ACTIVOS_BASE, DEVICES_ROUTE } from "../paths";

export type SaveErrorOutcome =
  | { kind: "fields"; errors: ValidationIssue[] }
  | { kind: "redirected" }
  | { kind: "handled" };

export interface ConflictTargets {
  navigate: (path: string) => void;
  /** Abre la compra existente en la pantalla de Compras. */
  openPurchase?: (purchaseId: string) => void;
  /** Abre el proveedor existente en la pantalla de Proveedores. */
  openSupplier?: (supplierId: string) => void;
}

export const assetPath = (assetId: string) => `${ACTIVOS_BASE}/inventario/${encodeURIComponent(assetId)}`;
export const purchasePath = (purchaseId: string) => `${ACTIVOS_BASE}/compras?compra=${encodeURIComponent(purchaseId)}`;
export const supplierPath = (supplierId: string) => `${ACTIVOS_BASE}/proveedores?proveedor=${encodeURIComponent(supplierId)}`;

/**
 * Resuelve un error de guardado: errores de campo, ir al registro existente
 * (si la persona acepta) o un aviso genérico.
 */
export async function handleSaveError(err: unknown, t: TFunction, targets: ConflictTargets): Promise<SaveErrorOutcome> {
  const e = assetsErrorOf(err);
  if (e.status === 422 && e.data?.errors?.length) return { kind: "fields", errors: e.data.errors };

  if (e.status === 409) {
    const go = async (key: string, run: () => void) => {
      const ok = await confirmDialog({
        title: t(`conflicts.${key}.title`),
        text: t(`conflicts.${key}.text`),
        confirmText: t(`conflicts.${key}.action`),
        cancelText: t("common.stay"),
        icon: "warning",
        tone: "warning",
      });
      if (ok) run();
      return ok ? ({ kind: "redirected" } as const) : ({ kind: "handled" } as const);
    };
    if (e.code === "IDENTIFICADOR_DUPLICADO" && e.data?.assetId) {
      const id = e.data.assetId;
      return go("duplicateSerial", () => targets.navigate(assetPath(id)));
    }
    if (e.code === "ES_DISPOSITIVO") {
      return go("isDevice", () => targets.navigate(DEVICES_ROUTE));
    }
    if (e.code === "COMPRA_DUPLICADA" && e.data?.purchaseId) {
      const id = e.data.purchaseId;
      return go("duplicatePurchase", () => (targets.openPurchase ? targets.openPurchase(id) : targets.navigate(purchasePath(id))));
    }
    if (e.code === "PROVEEDOR_DUPLICADO" && e.data?.supplierId) {
      const id = e.data.supplierId;
      return go("duplicateSupplier", () => (targets.openSupplier ? targets.openSupplier(id) : targets.navigate(supplierPath(id))));
    }
    if (e.code === "ESTADO_INVALIDO") {
      await notifyError(t("conflicts.invalidState.title"), t("conflicts.invalidState.text"));
      return { kind: "handled" };
    }
  }

  await notifyError(t("errors.save.title"), e.message || t("errors.save.text"));
  return { kind: "handled" };
}
